import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rankRewardService } from "@/services/rankRewardService";
import type { RankRewardCreateDto, RankRewardUpdateDto } from "@/types/rank-reward.dto";
import { queryKeys } from "./keys";

// ─── Admin: RankReward CRUD ──────────────────────────────────────────────────

export function useAdminRankRewards() {
  return useQuery({
    queryKey: queryKeys.rankReward.all,
    queryFn: () => rankRewardService.getAll(),
  });
}

export function useCreateRankRewardMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: RankRewardCreateDto) => rankRewardService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rankReward.all });
    },
  });
}

export function useUpdateRankRewardMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: RankRewardUpdateDto }) =>
      rankRewardService.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rankReward.all });
    },
  });
}

export function useDeleteRankRewardMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => rankRewardService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rankReward.all });
    },
  });
}

// ─── User: Rank Status & Claim ───────────────────────────────────────────────

export function useMyRankStatus() {
  return useQuery({
    queryKey: queryKeys.rankReward.myStatus,
    queryFn: () => rankRewardService.getMyRankStatus(),
    staleTime: 30_000, // refresh every 30 s
  });
}

export function useClaimRankRewardMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rankRewardId: number) => rankRewardService.claimReward(rankRewardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rankReward.myStatus });
    },
  });
}

// ─── Admin: Reward Requests ──────────────────────────────────────────────────

export function useAdminRewardRequests() {
  return useQuery({
    queryKey: queryKeys.rankReward.adminRequests,
    queryFn: () => rankRewardService.getAllRewardRequests(),
  });
}
